import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import PageHeader from '../../components/ui/PageHeader';
import Card from '../../components/ui/Card';
import Table from '../../components/ui/Table';
import { API_BASE_URL } from '../../services/apiClient';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Input from '../../components/ui/Input';
import Pagination from '../../components/ui/Pagination';
import ActionIconButton from '../../components/ui/ActionIconButton';
import DeleteModal from '../../components/ui/DeleteModal';
import TierBreakdownBar from '../../components/ui/TierBreakdownBar';

import { fetchTasks, deleteTask, recalculateTask } from '../../store/slices/tasksSlice';
import { fetchPeriods } from '../../store/slices/periodsSlice';
import { fetchSegments, fetchMudurlukler } from '../../store/slices/referencesSlice';
import { pushToast } from '../../store/slices/uiSlice';
import useJobWatcher from '../../hooks/useJobWatcher';
import { formatDate } from '../../utils/format';
import { METRIC_TYPES } from '../../constants/navigation';
import TaskFormWizard from './TaskFormWizard';
import ManualScoresDialog from './ManualScoresDialog';
import TaskTemplatePicker from './TaskTemplatePicker';
import TaskCancelModal from './TaskCancelModal';
import TaskUserStatesModal from './TaskUserStatesModal';

export default function Tasks() {
  const dispatch = useDispatch();
  const tasks = useSelector((s) => s.tasks.items);
  const taskStatus = useSelector((s) => s.tasks.status);
  const tasksPagination = useSelector((s) => s.tasks.pagination);
  const periods = useSelector((s) => s.periods.items);
  const segments = useSelector((s) => s.references.segments);
  const segmentsStatus = useSelector((s) => s.references.segmentsStatus);
  const mudurluklerStatus = useSelector((s) => s.references.mudurluklerStatus);
  const [filterPeriod, setFilterPeriod] = useState('');
  const [filterMetric, setFilterMetric] = useState('');
  // Görev adı arama — debounce sonrası backend'e `q` parametresi olarak gider.
  const [searchInput, setSearchInput] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(50);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [manualScoreTarget, setManualScoreTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [recalcTarget, setRecalcTarget] = useState(null);
  // Görev başına kullanıcı durum modali — eye-icon ile açılır.
  // null = kapalı, görev objesi = açık.
  const [userStatesTarget, setUserStatesTarget] = useState(null);
  // Template picker, yeni gorev olustururken "Yeni Gorev" butonu ile acilir.
  // Admin template sectiginde wizard otomatik acilir, initial degerler
  // template.build()'ten gelir.
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchPeriods({ page: 1, size: 200 }));
  }, [dispatch]);

  // Segment + müdürlük referans verileri — Hedef kolonu için isimleri çözmek
  // amacıyla bir defa yükleniyor (idle ise).
  useEffect(() => {
    if (segmentsStatus === 'idle') dispatch(fetchSegments({ include_inactive: false }));
    if (mudurluklerStatus === 'idle') dispatch(fetchMudurlukler());
  }, [dispatch, segmentsStatus, mudurluklerStatus]);

  // Search debounce — kullanıcı hızlı yazarken backend'e her tuşta istek atma.
  useEffect(() => {
    const id = setTimeout(() => setSearchQ(searchInput.trim()), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  // Filtre değişince ilk sayfaya dön.
  useEffect(() => {
    setPage(1);
  }, [filterPeriod, filterMetric, searchQ]);

  useEffect(() => {
    const params = { page, size };
    if (filterPeriod) params.period_id = Number(filterPeriod);
    if (filterMetric) params.metric_type = filterMetric;
    if (searchQ) params.q = searchQ;
    dispatch(fetchTasks(params));
  }, [dispatch, page, size, filterPeriod, filterMetric, searchQ]);

  // Yeniden hesaplama worker'da arka planda çalışır; iş bitince listeyi o anki
  // sayfa/filtrelerle yenile ki kademe ilerlemesi sayfa yenilemeden görünsün.
  const reloadTasksRef = useRef(null);
  reloadTasksRef.current = () => {
    const params = { page, size };
    if (filterPeriod) params.period_id = Number(filterPeriod);
    if (filterMetric) params.metric_type = filterMetric;
    if (searchQ) params.q = searchQ;
    dispatch(fetchTasks(params));
  };
  const watchJob = useJobWatcher();

  // Period lookup — Hedef kolonunda görevin segment_ids'i boşsa dönemden devral.
  const periodById = useMemo(() => {
    const m = new Map();
    (periods || []).forEach((p) => m.set(p.id, p));
    return m;
  }, [periods]);

  // Segment çözücü — backend görev/dönem `segment_ids` kayıtlarında
  // segment'in adını ya da id'sini saklayabilir. İkisini de eşleştir.
  const resolveSegmentLabel = (idOrName) => {
    const seg = (segments || []).find(
      (s) => s.name === idOrName || String(s.id) === String(idOrName),
    );
    return seg ? seg.name : String(idOrName || '');
  };

  // Backend zaten filtreli döner; ekstra client-side filtre gereksiz.
  const filtered = tasks;

  const metricBadge = (mt) => {
    const def = METRIC_TYPES.find((x) => x.value === mt);
    return <Badge color={def?.color || 'neutral'}>{def?.label || mt}</Badge>;
  };

  const columns = [
    {
      key: 'name',
      header: 'Görev',
      render: (r) => (
        <div className="flex items-center gap-3">
          {r.image_url ? (
            <img
              src={r.image_url?.startsWith('http') ? r.image_url : API_BASE_URL + r.image_url}
              alt=""
              className="flex-shrink-0 rounded-lg object-cover border border-neutral-200"
              style={{ width: 60, height: 40 }}
            />
          ) : (
            <div
              className="flex-shrink-0 rounded-lg border border-dashed border-neutral-200 bg-neutral-50 flex items-center justify-center"
              style={{ width: 60, height: 40 }}
            >
              <svg className="h-4 w-4 text-neutral-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          )}
          <div>
            <p className="font-medium text-neutral-900">{r.name}</p>
            {r.description && <p className="text-xs text-neutral-500 mt-0.5">{r.description}</p>}
          </div>
        </div>
      ),
    },
    {
      key: 'period',
      header: 'Dönem',
      render: (r) => {
        const period = periodById.get(r.game_period_id);
        // Görev özel tarih aralığı (effective_start_date / effective_end_date)
        // tanımlıysa görevin dönem içindeki yürürlük penceresini ayrıca göster.
        // Bu değerler dönem aralığının ALT KÜMESİ olur — admin ay içinde
        // 5-15 gibi dar bir pencere belirtebilir.
        const hasOverride = r.effective_start_date || r.effective_end_date;
        return (
          <div className="flex flex-col gap-0.5">
            <span className="text-xs text-neutral-700 font-medium">
              {period?.name || `#${r.game_period_id}`}
            </span>
            {hasOverride && (
              <span
                className="text-[10px] text-amber-700 italic"
                title="Görev özel tarih aralığı — bu görev sadece belirtilen tarihlerde değerlendirilir."
              >
                {formatDate(r.effective_start_date) || '?'} — {formatDate(r.effective_end_date) || '?'}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'metric_type',
      header: 'Metrik',
      render: (r) => metricBadge(r.metric_type),
    },
    {
      key: 'target',
      header: 'Hedef',
      render: (r) => {
        // Hedef = (segment) + (müdürlük) bilgisi.
        //
        // Segment: Görevin kendi `segment_ids`'i doluysa onu göster (görev-özel
        // hedefleme). Boş ise dönemden devral (`period.segment_ids`).
        // Müdürlük: Görevin `mudurluk_kodlari` null değilse görev seviyesinde
        // override; null ise dönemden devral. Boş liste = "tüm müdürlükler".
        const period = periodById.get(r.game_period_id);
        const taskSegIds = Array.isArray(r.segment_ids) ? r.segment_ids : [];
        const periodSegIds = Array.isArray(period?.segment_ids) ? period.segment_ids : [];
        const segIds = taskSegIds.length > 0 ? taskSegIds : periodSegIds;
        const segmentSource = taskSegIds.length > 0 ? 'task' : (periodSegIds.length > 0 ? 'period' : null);

        const taskMud = r.mudurluk_kodlari;
        const periodMud = period?.mudurluk_kodlari;
        // null → dönemden devral; [] → tüm müdürlükler; dolu → liste.
        const mudList = Array.isArray(taskMud) ? taskMud : (Array.isArray(periodMud) ? periodMud : null);
        const mudSource = Array.isArray(taskMud) ? 'task' : (Array.isArray(periodMud) ? 'period' : null);

        if (segIds.length === 0 && (!mudList || mudList.length === 0)) {
          return <span className="text-xs text-neutral-400">Tüm kullanıcılar</span>;
        }

        // Görev-özel hedefleme bilgisi (* yıldız) badge tooltip'iyle veriliyor
        // — etiket olarak ayrı satır göstermiyoruz, satır yüksekliğini sade tut.
        const segTooltip = segmentSource === 'task'
          ? 'Görev-özel segment'
          : 'Dönemden devralınan segment';
        const mudTooltip = mudSource === 'task'
          ? 'Görev-özel müdürlük'
          : 'Dönemden devralınan müdürlük';

        return (
          <div className="flex flex-col gap-1 max-w-[220px]">
            {segIds.length > 0 && (
              <div className="flex flex-wrap gap-1 items-center" title={segTooltip}>
                {segIds.slice(0, 3).map((id) => (
                  <Badge key={id} color="blue" size="sm">{resolveSegmentLabel(id)}</Badge>
                ))}
                {segIds.length > 3 && (
                  <span className="text-[10px] text-neutral-500">+{segIds.length - 3}</span>
                )}
              </div>
            )}
            {Array.isArray(mudList) && mudList.length > 0 && (
              <div className="flex flex-wrap gap-1 items-center" title={mudTooltip}>
                {mudList.slice(0, 2).map((kod) => (
                  <Badge key={kod} color="purple" size="sm">{kod}</Badge>
                ))}
                {mudList.length > 2 && (
                  <span className="text-[10px] text-neutral-500">+{mudList.length - 2}</span>
                )}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'tiers',
      header: 'Kademe',
      render: (r) => (
        <span className="text-xs text-neutral-600">
          {r.tiers?.length || 0} kademe · max {r.max_points}p
        </span>
      ),
    },
    {
      key: 'completion',
      header: 'Kademe İlerlemesi',
      render: (r) => {
        // Yeni görünüm: tier_breakdown — her kademede kaç oyuncu o eşiği geçti.
        // Eski tek yüzdelik gösterim (completion_percent) tooltip'te kalır,
        // tier'sız görevler (örn. MANUAL) için fallback olarak görünür.
        const tiers = Array.isArray(r.tier_breakdown) ? r.tier_breakdown : [];
        if (tiers.length === 0) {
          // Tier yoksa eski yüzde göstergesi (genelde MANUAL veya henüz tier
          // tanımlanmamış görevler için).
          const pct = r.completion_percent;
          if (pct == null) return <span className="text-[10px] text-neutral-400">—</span>;
          return (
            <div className="w-20" title={`Katılım oranı: %${pct}`}>
              <div className="h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="text-[10px] text-neutral-500">%{pct}</span>
            </div>
          );
        }
        return (
          <div className="min-w-[180px]">
            <TierBreakdownBar tiers={tiers} />
            {r.completion_percent != null && (
              <span className="text-[9px] text-neutral-400 mt-1 block">
                Katılım: %{r.completion_percent}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Durum',
      render: (r) => {
        const status = String(r.status || '').toUpperCase();
        const isCancelled = status === 'CANCELLED';
        // İptal politikası etiketi — backend `cancellation_policy` ile döner.
        // FULL_POINTS    → Tam Puan
        // CANCEL_REVOKE  → Puanlar Geri Alındı
        // REPLACE_RECALC → Yeniden Hesaplandı (legacy / manuel akış)
        const POLICY_LABELS = {
          FULL_POINTS: 'Tam Puan',
          CANCEL_REVOKE: 'Puanlar Geri Alındı',
          REPLACE_RECALC: 'Yeniden Hesaplandı',
        };
        const POLICY_TOOLTIPS = {
          FULL_POINTS: 'İptal politikası: Tüm katılımcılara maksimum puan verildi.',
          CANCEL_REVOKE: 'İptal politikası: Görev iptal edildi, verilen tüm puanlar geri alındı.',
          REPLACE_RECALC: 'İptal politikası: Görev yeniden hesaplandı (replacement task ile).',
        };
        const policy = String(r.cancellation_policy || '').toUpperCase();
        const policyLabel = POLICY_LABELS[policy];
        return (
          <div className="flex flex-col items-start gap-0.5">
            <Badge color={status === 'ACTIVE' ? 'green' : isCancelled ? 'red' : 'neutral'}>
              {status === 'ACTIVE' ? 'Aktif' : isCancelled ? 'İptal' : r.status}
            </Badge>
            {isCancelled && policyLabel && (
              <span
                className="text-[10px] text-neutral-500 italic"
                title={POLICY_TOOLTIPS[policy] || ''}
              >
                {policyLabel}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'actions',
      header: 'İşlemler',
      className: 'text-right',
      render: (r) => {
        const isManualActive =
          r.metric_type === 'MANUAL' &&
          String(r.status || '').toUpperCase() === 'ACTIVE';
        return (
          // zuzzuu_customers GridRowActions ile birebir: size-fit + rounded-lg +
          // #E8E8E8 kenar + 1.5 padding + soft shadow + size-5 ikon, semantic renk.
          <div className="flex items-center justify-end gap-1">
            {/* Göz — görev başına kullanıcı durumlarını gör (raw_value, points,
              * tier, bonus, total). Tüm statülerde aktif: ACTIVE'de canlı
              * görüntü, kapalı/iptal görevlerde tarihsel snapshot. */}
            <ActionIconButton
              icon="eye"
              iconColor="#0085FF"
              title="Kullanıcı Durumlarını Gör"
              onClick={() => setUserStatesTarget(r)}
            />
            {isManualActive && (
              <ActionIconButton
                icon="upload"
                iconColor="#513CFF"
                title="Manuel Skor Gönder"
                onClick={() => setManualScoreTarget(r)}
              />
            )}
            <ActionIconButton
              icon="edit"
              iconColor="#3CBFFF"
              title="Düzenle"
              onClick={() => setWizardOpen({ mode: 'edit', task: r })}
            />
            {String(r.status || '').toUpperCase() === 'ACTIVE' && (
              <>
                <ActionIconButton
                  icon="refresh-cw"
                  iconColor="#10b981"
                  title="Yeniden Hesapla"
                  onClick={() => setRecalcTarget(r)}
                  className="hover:bg-emerald-50 hover:border-emerald-200"
                />
                <ActionIconButton
                  icon="x-octagon"
                  iconColor="#f97316"
                  title="İptal Et"
                  onClick={() => setCancelTarget(r)}
                  className="hover:bg-orange-50 hover:border-orange-200"
                />
              </>
            )}
            <ActionIconButton
              icon="trash"
              iconColor="#FD5995"
              title="Sil"
              onClick={() => setDeleteTarget(r)}
            />
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Görev Yönetimi"
        subtitle="Dönemlerdeki rule_set tanımları — metrik tipi, kaynak tablo, kademeli puanlama"
        className="bg-white border border-[#E1E1E1] shadow-card rounded-[1.25rem] px-6 py-4"
        actions={
          // Yeni Gorev akisi: once template secimi, sonra wizard.
          // "Yeni Gorev" → templatePickerOpen=true → admin template secer →
          // onPick ile wizard acilir (initial = template.build()).
          <Button icon="plus" onClick={() => setTemplatePickerOpen(true)}>
            Yeni Görev
          </Button>
        }
      />

      <Card padded={false}>
        <div className="p-4 flex items-center gap-3 flex-wrap">
          <div className="w-full sm:w-auto sm:min-w-[16rem] flex-1 sm:flex-none">
            <Input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Görev adında ara…"
              className="bg-white"
            />
          </div>
          <Select
            value={filterPeriod}
            onChange={(e) => setFilterPeriod(e.target.value)}
            placeholder="Tüm dönemler"
            options={periods.map((p) => ({ value: p.id, label: p.name }))}
            className="w-auto min-w-[12rem]"
          />
          <Select
            value={filterMetric}
            onChange={(e) => setFilterMetric(e.target.value)}
            placeholder="Tüm metrikler"
            options={METRIC_TYPES.map((m) => ({ value: m.value, label: m.label }))}
            className="w-auto min-w-[12rem]"
          />
          {(searchInput || filterPeriod || filterMetric) && (
            <button
              type="button"
              onClick={() => {
                setSearchInput('');
                setFilterPeriod('');
                setFilterMetric('');
              }}
              className="text-xs text-neutral-500 hover:text-neutral-700 underline"
            >
              Filtreleri Temizle
            </button>
          )}
        </div>
        <Table
          columns={columns}
          rows={filtered}
          loading={taskStatus === 'loading'}
          emptyMessage={
            taskStatus === 'failed'
              ? 'Görev listesi alınamadı. Sunucuya ulaşılamıyor — servis yayına alındıktan sonra yeniden deneyin.'
              : 'Görev bulunamadı'
          }
        />
        <div className="px-4 pb-4 pt-2">
          <Pagination
            page={page}
            size={size}
            total={tasksPagination?.total ?? 0}
            onPageChange={setPage}
            onSizeChange={(newSize) => {
              setSize(newSize);
              setPage(1);
            }}
            loading={taskStatus === 'loading'}
          />
        </div>
      </Card>

      <TaskTemplatePicker
        open={templatePickerOpen}
        onClose={() => setTemplatePickerOpen(false)}
        onPick={(template) => {
          setTemplatePickerOpen(false);
          // Template'ten initial form degerleri uret. CUSTOM icin null;
          // wizard kendi emptyForm'unu kullanir. Diger template'ler icin
          // build() uygun default'lari doldurmus nesne doner.
          const initialTask = template.build ? template.build() : null;
          setWizardOpen({
            mode: 'create',
            task: initialTask,
            template,
          });
        }}
      />

      <TaskFormWizard
        open={!!wizardOpen}
        mode={wizardOpen?.mode}
        initial={wizardOpen?.task}
        template={wizardOpen?.template}
        onClose={() => setWizardOpen(false)}
      />

      <ManualScoresDialog
        task={manualScoreTarget}
        onClose={() => setManualScoreTarget(null)}
      />

      <DeleteModal
        open={!!recalcTarget}
        title="Görevi yeniden hesapla"
        description={
          recalcTarget
            ? `"${recalcTarget.name}" görevinin puanları, dönem yeniden hesaplanarak güncellenecek. Veri düzeltmelerinin sisteme yansıması için kullanın.`
            : ''
        }
        deleteLabel="Yeniden Hesapla"
        cancelLabel="Vazgeç"
        accentColor="#10b981"
        confirmIcon="refresh"
        onClose={() => setRecalcTarget(null)}
        onConfirm={async () => {
          if (!recalcTarget) return;
          const taskName = recalcTarget.name;
          const res = await dispatch(recalculateTask(recalcTarget.id));
          if (res.meta.requestStatus === 'fulfilled') {
            dispatch(pushToast({ kind: 'info', message: 'Yeniden hesaplama kuyruğa alındı.' }));
            watchJob(res.payload?.job_id, {
              label: `"${taskName}" yeniden hesaplaması`,
              onDone: () => reloadTasksRef.current?.(),
            });
          } else {
            dispatch(pushToast({ kind: 'error', message: res.payload || 'Bir hata oluştu.' }));
          }
        }}
      />

      <DeleteModal
        open={!!deleteTarget}
        title="Görevi silmek istediğinize emin misiniz?"
        description={
          deleteTarget
            ? `"${deleteTarget.name}" görevi kalıcı olarak silinecek. Bu işlem geri alınamaz.`
            : 'Bu işlem geri alınamaz.'
        }
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          if (!deleteTarget) return;
          const res = await dispatch(deleteTask(deleteTarget.id));
          if (res.meta.requestStatus === 'fulfilled') {
            dispatch(pushToast({ kind: 'success', message: 'Görev silindi' }));
          }
        }}
      />

      {cancelTarget && (
        <TaskCancelModal
          task={cancelTarget}
          onClose={() => setCancelTarget(null)}
          onSuccess={() => {
            setCancelTarget(null);
            const params = { page, size };
            if (filterPeriod) params.period_id = Number(filterPeriod);
            if (filterMetric) params.metric_type = filterMetric;
            dispatch(fetchTasks(params));
          }}
        />
      )}

      {/* Görev başına kullanıcı durum modali — eye-icon ile açılır. */}
      <TaskUserStatesModal
        open={!!userStatesTarget}
        task={userStatesTarget}
        onClose={() => setUserStatesTarget(null)}
      />
    </div>
  );
}
